export const FEATURES = [
  'Branded client portal',
  'Proposals with Stripe payments',
  'Client onboarding in minutes',
  'Shared Kanban boards',
  'Collaboration Hub',
  'Embed any dashboard: Looker, Tableau, GA4, and more',
  'Google Ads Previewer with AI assistant',
  'UTM builder clients can actually use',
  'White-label agency resource library',
  'Calendar booking built into the portal',
  'API and Zapier integrations',
  'White-label services when you need backup',
];

export interface FeatureItem {
  title: string;
  description: string;
  learnMoreHref?: string;
  image?: string;
}

export interface FeatureTab {
  id: string;
  label: string;
  items: FeatureItem[];
}

export const FEATURE_TABS: FeatureTab[] = [
  {
    id: 'save-time',
    label: 'Save Time',
    items: [
      {
        title: 'Collaboration Hub',
        description:
          'Notes, files, Looms, Figma designs, and progress updates in one shared space. No more scattered docs or endless email threads.',
        learnMoreHref: '/services',
        image: '/images/features/1-collaboration-hub.webp',
      },
      {
        title: 'Project Boards',
        description:
          'Shared project boards for your agency and your clients. Auto-generate tasks from specs or meetings, track progress in real time.',
        learnMoreHref: '/services',
        image: '/images/features/4-project-board.webp',
      },
      {
        title: 'Browser Extension',
        description:
          'Manage client work from any browser tab without switching tools. Log notes, view health, and access your CRM without context-switching.',
        learnMoreHref: '/services',
        image: '/images/features/plugin.webp',
      },
      {
        title: 'Client Onboarding',
        description:
          'Manage client credentials and share onboarding steps in minutes, not days.',
        learnMoreHref: '/services',
        image: '/images/features/2-onboarding.webp',
      },
    ],
  },
  {
    id: 'grow-revenue',
    label: 'Grow Revenue',
    items: [
      {
        title: 'Project Proposals',
        description:
          'Polished proposals with contracts, add-ons, and Stripe payments. Clients review, sign, and pay in one click.',
        learnMoreHref: '/services',
        image: '/images/features/2-proposals.webp',
      },
      {
        title: 'White-Label Services',
        description:
          'Analytics, marketing, SEO, CRO, and dev services - delivered under your brand. Scale without hiring.',
        learnMoreHref: '/services',
        image: '/images/features/3-white-label.webp',
      },
      {
        title: 'Client Health Tracking',
        description:
          'Monitor client activity, log internal notes, and flag at-risk relationships before they become problems.',
        learnMoreHref: '/services',
        image: '/images/features/3-client-health.webp',
      },
      {
        title: 'Calendar Booking',
        description:
          'Let clients book meetings directly inside their portal. No back-and-forth emails, no third-party links.',
        learnMoreHref: '/services',
        image: '/images/features/bookings.webp',
      },
    ],
  },
  {
    id: 'impress-clients',
    label: 'Impress Clients',
    items: [
      {
        title: 'Agency-Branded Portal',
        description:
          'Your domain. Your logo. Your brand. A client experience that builds trust and value.',
        learnMoreHref: '/services',
        image: '/images/features/1-client-portal.webp',
      },
      {
        title: 'Client Feedback',
        description:
          'Collect testimonials across platforms, display them in one place, and reward clients who spread the word.',
        learnMoreHref: '/services',
        image: '/images/features/2-client-feedback.jpg',
      },
      {
        title: 'Reporting Dashboards',
        description:
          'Embed Looker, Tableau, PostHog, GA4, and more. All dashboards in one clean, branded view.',
        learnMoreHref: '/services',
        image: '/images/features/3-reporting.webp',
      },
      {
        title: 'Google Ads Preview',
        description:
          'Share Google Ads campaign drafts, generate display ad sizes, manage UTM links. Get campaign approvals without the back and forth.',
        learnMoreHref: '/services',
        image: '/images/features/4-marketing.webp',
      },
    ],
  },
];


export interface PricingTier {
  name: string;
  price: number; // monthly price
  minClients: number;
  maxClients: number | null; // null means unlimited
  features: string[];
}

export const PRICING_TIERS: PricingTier[] = [
  {
    name: 'Free',
    price: 0,
    minClients: 1,
    maxClients: 3,
    features: [
      '2 Team Seats',
      'Your Agency Branding (limited)',
    ],
  },
  {
    name: 'Pro',
    price: 45,
    minClients: 4,
    maxClients: 20,
    features: [
      '3 Team Seats',
      'Your Agency Branding',
    ],
  },
  {
    name: 'Max',
    price: 90,
    minClients: 21,
    maxClients: null,
    features: [
      'Unlimited Team Seats',
      'Your Agency Branding',
    ],
  },
];

export interface ResourceCard {
  title: string;
  image: string;
  date: string;
  link: string;
}

export const AGENCY_RESOURCES = {
  heading: 'Agency Resources',
  description: 'Practical guides on analytics, marketing, AI, and agency tools. Real answers to real problems. Learn what actually works.',
  ctaText: 'View All Resources',
  ctaLink: '/resources',
  cards: [] as ResourceCard[],
};
